import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
	Button,
	Stepper,
	Step,
	StepLabel,
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	CircularProgress,
	TextField,
	Autocomplete,
	Divider,
	Paper,
	Stack,
} from '@mui/material'
import WorkItemTemplateTable from './WorkItemTemplateTable'
import { useWorkItemTemplates } from './WorkItemTemplatesContext'
import { useWorkItemBuilder } from './useWorkItemBuilder'
import { flattenTemplates } from '../../utils/ScaffoldWorkItemUtils'
import { ScaffoldWorkItem } from '../../types/Scaffold/Scaffold'
import { Spec } from '../../types/Project/Spec'
import { showSuccessToast } from '../../services/CommonServices/alertServices'

interface CreateWorkItemProps {
	onCreate: (
		workItemData: ScaffoldWorkItem[],
		metadataValues: Record<number, string>,
	) => Promise<void>
	refresh: () => Promise<void>
	scaffoldRequest: any
	addChildtoId?: number
}

const steps = ['Select WBS', 'Work Item Details', 'Spec & Metadata']

export default function CreateWorkItem({ 
	scaffoldRequest,
	onCreate, 
	refresh, 
	addChildtoId,
}: CreateWorkItemProps) {
	const [activeStep, setActiveStep] = useState(0)
	const [selectedSpec, setSelectedSpec] = useState<Spec | null>(null)
	const [metadataValues, setMetadataValues] = useState<Record<number, string>>({})
	const [isCreating, setIsCreating] = useState(false)
	
	// Track last initialized values to prevent unnecessary re-initialization
	const lastInitializedRef = useRef<{ wbsId?: number; unitOfMeasure?: string }>({})

	// Context for template state management
	const {
		workItemTemplates,
		selectedWBS,
		unitOfMeasure,
		addWorkItemTemplate,
		removeWorkItemTemplate,
		updateWorkItemTemplate,
		setUnitOfMeasure,
		setSelectedWBS,
		setWorkItemTemplates,
		getIncrementId,
		resetIdCounter,
	} = useWorkItemTemplates()

	// Stable callback for hierarchy built notification
	const onHierarchyBuilt = useCallback(() => {
		// Only auto-advance to step 1 when adding a child to existing hierarchy
		// For new work items, user should manually click "Next"
		if (addChildtoId) {
			setActiveStep(1)
		}
	}, [addChildtoId])

	// Hook for data fetching and business logic
	const {
		wbsData,
		metadataTypes,
		specs,
		isLoading,
		initializeHierarchy,
		existingHierarchy,
		existingHierarchyLoading,
	} = useWorkItemBuilder({
		scaffoldRequest,
		addChildtoId,
		onHierarchyBuilt,
	})

	// Auto-select WBS and unit of measure when adding a child
	useEffect(() => {
		if (addChildtoId && existingHierarchy.length > 0 && wbsData.length > 0) {
			const firstItem = existingHierarchy[0]
			const matchingWBS = wbsData.find((wbs) =>
				wbs.workItemTypes.some((type) => type.id === firstItem.workItemTypeId),
			)
			if (matchingWBS && !selectedWBS) {
				setSelectedWBS(matchingWBS)
				setUnitOfMeasure(firstItem.unitOfMeasure)
			}
		}
	}, [addChildtoId, existingHierarchy, wbsData, selectedWBS, setSelectedWBS, setUnitOfMeasure])

	// Auto-initialize hierarchy when adding a child (addChildtoId is provided)
	// Reinitializes automatically when WBS or unitOfMeasure changes
	// For new work items, hierarchy will be initialized when user clicks "Next"
	useEffect(() => {
		if (addChildtoId && selectedWBS && unitOfMeasure) {
			// Check if we need to reinitialize (WBS or unitOfMeasure changed)
			const needsReinit = 
				lastInitializedRef.current.wbsId !== selectedWBS.id ||
				lastInitializedRef.current.unitOfMeasure !== unitOfMeasure
			
			if (needsReinit) {
				// Reset counter and get increment function before initializing
				resetIdCounter()
				const templates = initializeHierarchy(
					selectedWBS,
					unitOfMeasure,
					existingHierarchy.length > 0 ? existingHierarchy : undefined,
					getIncrementId(),
				)
				setWorkItemTemplates(templates)
				// Update ref to track what we just initialized
				lastInitializedRef.current = {
					wbsId: selectedWBS.id,
					unitOfMeasure: unitOfMeasure,
				}
			}
		}
	}, [addChildtoId, selectedWBS, unitOfMeasure, existingHierarchy, initializeHierarchy])

	const handleNext = () => {
		if (activeStep === steps.length - 1) {
			handleCreate()
		} else {
			// When moving from step 0 to step 1 for NEW work items (not adding child),
			// initialize the hierarchy here since it wasn't auto-initialized
			if (activeStep === 0 && !addChildtoId && selectedWBS && unitOfMeasure) {
				// Reset counter and get increment function before initializing
				resetIdCounter()
				const templates = initializeHierarchy(
					selectedWBS,
					unitOfMeasure,
					undefined, // No existing hierarchy for new work items
					getIncrementId(),
				)
				setWorkItemTemplates(templates)
			}
			setActiveStep((prevActiveStep) => prevActiveStep + 1)
		}
	}

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1)
	}

	const handleCreate = async () => {
		setIsCreating(true)
		try {
			// Flatten hierarchical structure for server communication
			const flatTemplates = flattenTemplates(workItemTemplates)

			const workItemData: ScaffoldWorkItem[] = flatTemplates.map((template) => ({
				workItemTypeId: template.wbsWorkItemType.id,
				specId: selectedSpec?.id || 0,
				name: template.name,
				quantityBudgeted: template.quantityBudgeted,
				unitOfMeasure: template.unitOfMeasure,
				releaseDate: template.releaseDate ? new Date(template.releaseDate).toISOString().split('T')[0] : null,
				nocDate: template.nocDate ? new Date(template.nocDate).toISOString().split('T')[0] : null,
				budgetedHours: template.budgetedHours,
				parentId: template.parentId || null,
				id: template.id,
			}))

			await onCreate(workItemData, metadataValues)
			await refresh()
			showSuccessToast('Work Item created successfully', true)
		} catch (error) {
			console.error('Error creating work item:', error)
		} finally {
			setIsCreating(false)
		}
	}

	const getStepContent = (step: number) => {
		switch (step) {
			case 0:
				return (
					<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
						<Typography variant='h6' gutterBottom>
							Select Work Breakdown Structure (WBS)
						</Typography>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
							Choose the WBS category for your new work item.
						</Typography>
						<FormControl fullWidth sx={{ mb: 3 }}>
							<InputLabel id='wbs-select-label'>WBS Category</InputLabel>
							<Select
								sx={{
									'& .MuiSelect-select': {
										textAlign: 'left'
									}
								}}
								labelId='wbs-select-label'
								value={selectedWBS?.id || ''}
								label='WBS Category'
								disabled={!!addChildtoId}
								onChange={(e) => {
									const wbsId = e.target.value as number
									const wbs = wbsData.find((w) => w.id === wbsId)
									if (wbs) {
										setSelectedWBS(wbs)
									}
								}}
							>
								{wbsData.map((wbs) => (
									<MenuItem key={wbs.id} value={wbs.id}>
										{wbs.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<TextField
							fullWidth
							label='Unit of Measure'
							value={unitOfMeasure}
							onChange={(e) => setUnitOfMeasure(e.target.value)}
							placeholder='e.g., SF, LF, EA, etc.'
							disabled={!!addChildtoId}
							sx={{ mb: 3 }}
						/>
						{selectedWBS && (
							<Box sx={{ alignSelf: 'flex-start' }}>
								<Typography variant='body1' gutterBottom>
									Available Work Item Types:
								</Typography>
								{selectedWBS.workItemTypes.map((type) => (
									<Typography key={type.id} variant='body2' color='text.secondary' sx={{ textAlign: 'left' }}>
										• {type.name}
									</Typography>
								))}
							</Box>
						)}
					</Box>
				)
			case 1:
				return (
					<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<Typography variant='h6' gutterBottom>
							Work Item Details
						</Typography>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
							Configure the details for your work items.
						</Typography>
						<WorkItemTemplateTable
							isScaffoldBased={scaffoldRequest !== null}
							selectedWBS={selectedWBS}
							workItemTemplates={workItemTemplates}
							onAddTemplate={addWorkItemTemplate}
							onRemoveTemplate={removeWorkItemTemplate}
							onUpdateTemplate={updateWorkItemTemplate}
							existingHierarchy={addChildtoId ? existingHierarchy : []}
						/>
					</Box>
				)
			case 2:
				return (
					<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<Typography variant='h6' gutterBottom>
							Spec & Metadata
						</Typography>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
							Select a spec and optionally fill out metadata for your work items.
						</Typography>

						{/* Spec Selection Section */}
						<Box sx={{ mb: 4, alignSelf: 'flex-start', width: '100%' }}>
							<Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', textAlign: 'left' }}>
								Spec
							</Typography>
							<Autocomplete
								options={specs}
								getOptionLabel={(option) => option.name}
								value={selectedSpec}
								onChange={(_, newValue) => setSelectedSpec(newValue)}
								renderInput={(params) => (
									<TextField
										{...params}
										label='Select Spec'
										placeholder='Search for a spec...'
										required
									/>
								)}
								renderOption={(props, option) => (
									<Box component='li' {...props}>
										<Box>
											<Typography variant='body1'>{option.name}</Typography>
										</Box>
									</Box>
								)}
								isOptionEqualToValue={(option, value) => option.id === value?.id}
							/>
						</Box>

						<Divider sx={{ mb: 4 }} />

						{/* Metadata Section */}
						<Box sx={{ alignSelf: 'flex-start', width: '100%' }}>
							<Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', textAlign: 'left' }}>
								Metadata
							</Typography>
							{metadataTypes.length > 0 ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
									{metadataTypes.map((metadata) => (
										<TextField
											key={metadata.id}
											fullWidth
											label={metadata.metadataName}
											value={metadataValues[metadata.id] || ''}
											onChange={(e) =>
												setMetadataValues((prev) => ({
													...prev,
													[metadata.id]: e.target.value,
												}))
											}
											placeholder={`Enter ${metadata.metadataName.toLowerCase()}`}
										/>
									))}
								</Box>
							) : (
								<Typography variant='body2' color='text.secondary'>
									No metadata types available for this project.
								</Typography>
							)}
						</Box>
					</Box>
				)
			default:
				return 'Unknown step'
		}
	}

	const canProceed = () => {
		switch (activeStep) {
			case 0:
				return selectedWBS !== null && unitOfMeasure.trim() !== ''
			case 1:
				const flatTemplates = flattenTemplates(workItemTemplates)
				return (
					flatTemplates.length > 0 &&
					flatTemplates.every(
						(template) =>
							template.name.trim() !== '' &&
							template.quantityBudgeted > 0 &&
							template.unitOfMeasure.trim() !== '' &&
							template.budgetedHours > 0,
					)
				)
			case 2:
				return selectedSpec !== null
			default:
				return false
		}
	}



	return (
		<Paper
			variant='outlined'
			sx={{
				p: 2,
				backgroundColor: '#f8f9fa',
				border: '1px solid #e0e0e0',
			}}
		>
			{(isLoading || existingHierarchyLoading) && activeStep === 0 ? (
				<CircularProgress size={24} sx={{ m: 0, p: 0 }} />
			) : (
				<Stack spacing={2}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
						<Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main' }}>
							Create Work Item
						</Typography>
					</Box>
					<Divider />
					<Stepper activeStep={activeStep} sx={{ mb: 3, justifyContent: 'flex-start' }}>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
					<Box>{getStepContent(activeStep)}</Box>
					<Box>
						<Button
							disabled={activeStep === 0 || isLoading || existingHierarchyLoading || isCreating
								|| (addChildtoId !== undefined && activeStep === 1)}
							onClick={handleBack}
							sx={{ mr: 1 }}
							variant='contained'
							color='primary'
						>
							Back
						</Button>
						{isLoading || existingHierarchyLoading || isCreating ? (
							<CircularProgress size={24} sx={{ m: 0, p: 0 }} />
						) : (
							<Button
								variant='contained'
								color='success'
								onClick={handleNext}
								disabled={!canProceed()}
							>
								{activeStep === 2 ? 'Create Work Item' : 'Next'}
							</Button>
						)}
					</Box>
				</Stack>
			)}
		</Paper>
	)
}
